import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import AuthTextField from "../../components/Form/AuthTextField";
import AuthPasswordField from "../../components/Form/AuthPasswordField";
import PasswordRequirements from "../../components/Form/PasswordRequirements";
import ResetPasswordStepIndicator, {
  type ResetPasswordStep,
} from "../../components/Form/ResetPasswordStepIndicator";
import {
  resetPasswordAPI,
  sendPasswordCodeAPI,
  verifyPasswordCodeAPI,
} from "../../api/auth";
import Spinner from "../../components/Spinner/Spinner";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { validatePassword } from "../../utils/passwordPolicy";

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";

function ForgotPassword() {
  const [step, setStep] = useState<ResetPasswordStep>("email");
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  function clearFeedback() {
    setError("");
    setMessage("");
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await sendPasswordCodeAPI({ email: email.trim() });
      if (response.ok) {
        setStep("code");
        setMessage("We sent a 6-digit reset code to your email.");
      } else {
        setError(response.message ?? "Failed to send reset code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    clearFeedback();
    setIsSendingCode(true);
    try {
      const response = await sendPasswordCodeAPI({ email: email.trim() });
      if (response.ok) {
        setMessage("A new reset code has been sent to your email.");
      } else {
        setError(response.message ?? "Failed to resend reset code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    if (!/^\d{6}$/.test(resetCode)) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setIsVerifying(true);
    try {
      const response = await verifyPasswordCodeAPI({
        email: email.trim(),
        code: resetCode,
      });
      if (response.ok) {
        setStep("password");
        setMessage("Code verified. Choose a new password below.");
      } else {
        setError(response.message ?? "Code verification failed.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    const passwordErr = validatePassword(newPassword);
    if (passwordErr) {
      setError(passwordErr);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setIsResetting(true);
    try {
      const response = await resetPasswordAPI({
        email: email.trim(),
        code: resetCode,
        newPassword,
      });
      if (response.ok) {
        setMessage("Password updated. Redirecting to sign in…");
        setTimeout(() => navigate("/login"), 2500);
      } else {
        setError(response.message ?? "Failed to reset password.");
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="eh-page mx-auto py-8 mb-24 px-2 max-w-[480px]">
      <div className="eh-card shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Events House
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Reset password</h1>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          Enter your email and we&apos;ll send a code to reset your password.
        </p>

        <ResetPasswordStepIndicator currentStep={step} />

        {step === "email" && (
          <form onSubmit={handleSendCode} onChange={clearFeedback}>
            <AuthTextField
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              required
            />
            <button type="submit" className={primaryBtnClass} disabled={isSendingCode}>
              {isSendingCode ? <Spinner /> : "Send reset code"}
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleVerifyCode} onChange={clearFeedback}>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 mb-4">
              <p className="font-medium text-slate-900 mb-1">Check your inbox</p>
              <p>
                Enter the 6-digit code we sent to <strong>{email}</strong>.
              </p>
            </div>
            <label className="block mb-4">
              <span className="block text-sm font-medium text-slate-700 mb-1.5">
                Reset code
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="w-full text-center text-2xl tracking-[0.4em] border border-slate-300 bg-white px-3 py-3 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={resetCode}
                onChange={(e) =>
                  setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                required
              />
            </label>
            <button type="submit" className={primaryBtnClass} disabled={isVerifying}>
              {isVerifying ? <Spinner /> : "Verify code"}
            </button>
            <div className="flex flex-col sm:flex-row gap-3 justify-center text-sm mt-4">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isSendingCode}
                className="text-slate-700 underline hover:text-slate-900 font-medium"
              >
                {isSendingCode ? "Sending…" : "Resend code"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  clearFeedback();
                }}
                className="text-slate-700 underline hover:text-slate-900 font-medium"
              >
                Change email
              </button>
            </div>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={handleResetPassword} onChange={clearFeedback}>
            <AuthPasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              required
            />
            <PasswordRequirements password={newPassword} />
            <AuthPasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
            />
            <button type="submit" className={primaryBtnClass} disabled={isResetting}>
              {isResetting ? <Spinner /> : "Update password"}
            </button>
          </form>
        )}

        {error && <ErrorAlert className="mt-4">{error}</ErrorAlert>}
        {message && <p className="text-green-700 text-sm mt-4">{message}</p>}

        <p className="text-sm text-slate-600 mt-6 pt-6 border-t border-slate-100">
          Remember your password?{" "}
          <NavLink to="/login" className="font-semibold text-slate-900 underline">
            Sign in
          </NavLink>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;
